<script>
    import { onMount } from "svelte";

    import {
        allGroups,
        loggedInDoctorDetail,
        prePopulateAccountOptions,
        user,
    } from "../../stores";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import {
        cacheAccountPrePopulateSearchOptions,
        cacheAllGroups,
        cachePreOpInstructions,
    } from "../../Utils/utilCache.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getText,
        goBack,
        goToURL,
        isObjectEmpty,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import {
        Button,
        CardLayout,
        Loader,
        NotificationModal,
        PageHead,
        PageLoading,
        RichTextEditor,
    } from "../../Components/Common/index.svelte";

    import { editIcon } from "../../Utils/utilConstants.svelte";

    export let params = {};

    $: submitBtnDisabled = _checkSubmitBtnDisabled(preOpInstruction);

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;
    $: isUserSupport = $user.role == DirectUserRoles.Support;
    $: isUserManager = $user.role == DirectUserRoles.Manager;
    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    $: isUserEnableSelectGroup = isUserAdmin || isUserSupport;
    $: isUserEnableSelectDoctor = isUserEnableSelectGroup || isUserManager;

    $: btnOptions = [
        {
            iconName: "editIcon",
            iFont: editIcon,
            btnText: "Edit",
            iconFunction: () => (isEditMode = true),
            isHidden:
                isEditMode ||
                (isUserManager && preOpInstruction?.groupId != $user.groupId) ||
                (isUserDoctor &&
                    preOpInstruction?.accountId != $loggedInDoctorDetail.id),
        },
    ];

    let isLoadingPreOpInstruction = false,
        preOpInstruction = {},
        backUpPreOpInstruction = {},
        isEditMode = false,
        isCancelEditModalOpen = false,
        isUpdatingPreOpInstruction = false;

    let preparingListOptions = false;

    let selectedGroup = {},
        selectedDoctor = {};

    onMount(async () => {
        if (params.query)
            isEditMode = params.query
                .split("&")
                .some(
                    (q) => q.split("=")[0] == "isEdit" && q.split("=")[1] == 1,
                );

        await loadPreOpInstruction();

        await initListOptions();
    });

    const goToPreOpInstructionsList = () => goToURL(`/PreOpInstructions`);

    const loadPreOpInstruction = async () => {
        isLoadingPreOpInstruction = true;

        await apiMethods.getMethod
            .getPreOpInstructionById(params.id)
            .then((res) => {
                if (!res || res.error) {
                    showError(
                        res.error ??
                            "Failed to load PreOp Instruction details, please refresh the page and try again and contact support if the issue persists.",
                    );
                    goBack();
                } else {
                    preOpInstruction = res;
                    backUpPreOpInstruction = res;
                }
            });

        isLoadingPreOpInstruction = false;
    };

    const initListOptions = async () => {
        preparingListOptions = true;

        if (isUserEnableSelectGroup) {
            await cacheAllGroups();
            if (preOpInstruction.groupId)
                selectedGroup.data =
                    $allGroups.find(
                        (group) => group.id == preOpInstruction.groupId,
                    ) ?? {};
        }

        if (isUserEnableSelectDoctor) {
            await cacheAccountPrePopulateSearchOptions();

            if (preOpInstruction.accountId)
                selectedDoctor.data =
                    $prePopulateAccountOptions.find(
                        (doctor) => doctor.id == preOpInstruction.accountId,
                    ) ?? {};
        }

        preparingListOptions = false;
    };

    const updatePreOpInstruction = async () => {
        _mapPreOpInstructionData();

        isUpdatingPreOpInstruction = true;

        await apiMethods.putMethod
            .updatePreOpInstruction(preOpInstruction)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update PreOp Instruction, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    preOpInstruction = { ...res };
                    backUpPreOpInstruction = { ...res };
                    isEditMode = false;
                    await cachePreOpInstructions(true);
                }
            });

        isUpdatingPreOpInstruction = false;
    };

    const clearSelectedGroup = () => (selectedGroup = {});

    const clearSelectedDoctor = () => (selectedDoctor = {});

    const cancelEdit = () => {
        preOpInstruction = { ...backUpPreOpInstruction };

        isEditMode = false;

        _closeConfirmCancelModal();
    };

    // HELPER FUNCTIONS
    const _checkSubmitBtnDisabled = (data = {}) => {
        if (!data || isObjectEmpty(data)) return true;

        return !data.name;
    };

    const _mapPreOpInstructionData = () => {
        if (isUserManager || isUserDoctor)
            preOpInstruction.groupId = $user.groupId ?? Number(0);

        if (isUserDoctor) {
            preOpInstruction.groupId = $user.groupId ?? Number(0);
            preOpInstruction.accountId = $loggedInDoctorDetail.id ?? Number(0);
        } else if (isUserManager) {
            preOpInstruction.groupId = $user.groupId ?? Number(0);
            if (!selectedDoctor || !selectedDoctor.data)
                delete preOpInstruction.accountId;
            else preOpInstruction.accountId = selectedDoctor.data.id;
        } else {
            if (!selectedGroup || !selectedGroup.data)
                delete preOpInstruction.groupId;
            else preOpInstruction.groupId = selectedGroup.data.id;
        }
    };

    const _openConfirmCancelModal = () => (isCancelEditModalOpen = true);

    const _closeConfirmCancelModal = () => (isCancelEditModalOpen = false);
</script>

<PageHead
    title={`${isEditMode ? "Edit" : "View"} Pre-Op Instruction`}
    backFunc={goToPreOpInstructionsList}
/>

<CardLayout title="Instruction Details" {btnOptions}>
    {#if isLoadingPreOpInstruction}
        <PageLoading />
    {:else}
        <div class="flex flex-1 flex-col gap-y-2 w-full">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <div class="flex flex-col flex-1 gap-y-4 h-auto">
                    <div class="flex gap-x-4 items-center align-center">
                        <label for="name" class="shortTitle"> Name </label>

                        {#if isEditMode}
                            <input
                                type="text"
                                class="input input-bordered input-sm flex flex-1"
                                id="name"
                                bind:value={preOpInstruction.name}
                            />
                        {:else}
                            <p class="flex flex-1">
                                {getText(preOpInstruction.name)}
                            </p>
                        {/if}
                    </div>

                    {#if isUserEnableSelectGroup}
                        <div class="flex gap-x-4 items-center align-center">
                            <label for="name" class="shortTitle"> Group </label>

                            {#if preparingListOptions}
                                <div class="flex flex-1 gap-x-2 items-center">
                                    <Loader />
                                    <p>Loading group</p>
                                </div>
                            {:else}
                                <p class="flex flex-1">
                                    {getText(selectedGroup?.data?.name ?? "-")}
                                </p>
                            {/if}
                        </div>
                    {/if}

                    {#if isUserEnableSelectDoctor}
                        <div class="flex gap-x-4 items-center align-center">
                            <label for="name" class="shortTitle">
                                Doctor
                            </label>

                            {#if preparingListOptions}
                                <div class="flex flex-1 gap-x-2 items-center">
                                    <Loader />
                                    <p>Loading doctor</p>
                                </div>
                            {:else}
                                <p class="flex flex-1">
                                    {getText(
                                        selectedDoctor?.data?.fullName ?? "-",
                                    )}
                                </p>
                            {/if}
                        </div>
                    {/if}
                </div>

                <div class="divider lg:divider-horizontal" />

                <div class="flex flex-col flex-1 gap-y-4 h-auto">
                    <div class="flex gap-x-4 items-center align-center">
                        <label
                            for="attachToEstimateEmail"
                            class="title cursor-pointer"
                        >
                            Attach to Estimate Email
                        </label>

                        <input
                            type="checkbox"
                            id="attachToEstimateEmail"
                            class="toggle toggle-secondary"
                            bind:checked={preOpInstruction.attachToEstimateEmail}
                            disabled={!isEditMode}
                        />
                    </div>

                    <div class="flex gap-x-4 items-center align-center">
                        <label
                            for="addToEstimatePdf"
                            class="title cursor-pointer"
                        >
                            Add To Estimate PDF
                        </label>

                        <input
                            type="checkbox"
                            id="addToEstimatePdf"
                            class="toggle toggle-secondary"
                            bind:checked={preOpInstruction.addToEstimatePdf}
                            disabled={!isEditMode}
                        />
                    </div>

                    <div class="flex gap-x-4 items-center align-center">
                        <label for="addToPortal" class="title cursor-pointer">
                            Add to Portal
                        </label>

                        <input
                            type="checkbox"
                            id="addToPortal"
                            class="toggle toggle-secondary"
                            bind:checked={preOpInstruction.addToPortal}
                            disabled={!isEditMode}
                        />
                    </div>
                </div>
            </div>

            <div class="flex flex-col flex-1 gap-y-2 w-full px-2 py-4">
                <p>Content</p>
                <div class="w-full break-words">
                    <RichTextEditor
                        bind:content={preOpInstruction.content}
                        config={{
                            isDisabled: !isEditMode,
                            style: {
                                minHeight: "220px"
                            },
                        }}
                    />
                </div>
            </div>
        </div>

        {#if isEditMode}
            <div class="flex flex-1 items-center justify-end p-4 gap-4">
                <Button
                    clickFunc={_openConfirmCancelModal}
                    manualClass="w-[64px] btn-error"
                >
                    Cancel
                </Button>

                <Button
                    clickFunc={updatePreOpInstruction}
                    manualClass={submitBtnDisabled
                        ? "w-[64px] text-primary"
                        : "w-[64px]"}
                    isDisabled={submitBtnDisabled}
                >
                    Update
                </Button>
            </div>
        {/if}
    {/if}
</CardLayout>

<NotificationModal
    isModalOpen={isCancelEditModalOpen}
    title="Cancel Edit"
    closeModal={cancelEdit}
    closeModalFunction={_closeConfirmCancelModal}
    cancelText="Discard changes"
    confirmAction={_closeConfirmCancelModal}
    submitText="Save and continue"
>
    <p class="leading-6 p-4">Discard all changes</p>
</NotificationModal>

<style>
    .shortTitle {
        width: 80px;
    }

    .title {
        width: 200px;
    }
</style>
